/**
 * <product-page>: variant selection, add to cart, sticky bar, gallery counter,
 * delivery date. One form and one submit handler; the sticky button submits the
 * same form through its `form` attribute.
 */
(() => {
  if (customElements.get('product-page')) return;

  /** "79,95 €". Mirrors snippets/money.liquid. */
  const formatMoney = (cents) => {
    const [int, dec] = (cents / 100).toFixed(2).split('.');
    return `${int.replace(/\B(?=(\d{3})+(?!\d))/g, '.')},${dec} €`;
  };

  /**
   * Delivery: today +3 days on weekdays, +4 on weekends.
   * A Saturday result moves to Friday, a Sunday result to Monday.
   */
  /**
   * Delivery: today +3 days on working days, +4 on weekends and holidays.
   * A Saturday result moves to Friday, a Sunday result to Monday; then any
   * weekend day or holiday (theme setting, Spain + Catalonia) is skipped.
   */
  const isoDate = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

  const estimateDelivery = (holidays = new Map(), from = new Date()) => {
    const isWeekend = (d) => d.getDay() === 0 || d.getDay() === 6;
    const off = (d) => isWeekend(d) || holidays.has(isoDate(d));
    const today = new Date(from.getFullYear(), from.getMonth(), from.getDate());
    const d = new Date(today);
    d.setDate(d.getDate() + (off(today) ? 4 : 3));
    if (d.getDay() === 6) d.setDate(d.getDate() - 1);
    if (d.getDay() === 0) d.setDate(d.getDate() + 1);
    let holiday = null;
    // Each weekday holiday between today and the estimate adds a day
    for (let i = 1; i < 7; i += 1) {
      const check = new Date(today);
      check.setDate(check.getDate() + i);
      if (check >= d) break;
      if (!isWeekend(check) && holidays.has(isoDate(check))) {
        d.setDate(d.getDate() + 1);
        holiday = holiday || check;
      }
    }
    // Never deliver on a weekend or holiday
    for (let i = 0; i < 7 && off(d); i += 1) {
      if (holidays.has(isoDate(d))) holiday = holiday || new Date(d);
      d.setDate(d.getDate() + 1);
    }
    return { date: d, holiday: holiday && { day: holiday.getDate(), name: holidays.get(isoDate(holiday)) } };
  };

  class ProductPage extends HTMLElement {
    connectedCallback() {
      this.data = JSON.parse(this.querySelector('[data-product-json]').textContent);
      this.strings = this.data.strings;
      this.form = this.querySelector('[data-product-form]');
      this.form.noValidate = true;
      this.fieldsets = [...this.querySelectorAll('[data-option]')];
      this.buttons = [...this.querySelectorAll('[data-atc]')];
      this.mainButton = this.querySelector('[data-main-atc]');
      this.threshold = Number(this.dataset.threshold);
      this.klarnaMin = Number(this.dataset.klarnaMin);
      this.lowStock = Number(this.dataset.lowStock);
      this.busy = false;

      this.form.addEventListener('change', () => this.update({ fromUser: true }));
      this.form.addEventListener('submit', (event) => this.onSubmit(event));
      this.isQuick = this.classList.contains('qa');

      // Quick-add pop-up extras: "Comprar ahora" and the quantity stepper
      const buyNow = this.querySelector('[data-buy-now]');
      if (buyNow) {
        buyNow.addEventListener('click', () => {
          if (this.busy) return;
          if (!this.variant) return this.pointToMissingOption();
          if (this.variant.available) this.addToCart({ buyNow: true });
        });
      }
      this.addEventListener('click', (event) => {
        const step = event.target.closest('[data-qty-step]');
        if (!step) return;
        const input = this.form.querySelector('input[name="quantity"]');
        input.value = Math.max(1, (Number(input.value) || 1) + Number(step.dataset.qtyStep));
      });

      this.setupSizeGuide();
      this.setupRestock();
      this.setupSheet();
      this.setupGallery();
      this.setupSticky();
      this.refreshDelivery();
      this.update();

      // First-order reminder applied (assets/nudge.js): show the prices with the discount
      if (!this.isQuick) {
        document.addEventListener('brisato:offer', (event) => {
          this.dataset.offerPct = String(event.detail.percent);
          this.update();
        });
      }
    }

    /** Price after the cart's percentage code, rounded like Shopify (discount floored). */
    offerPrice(cents) {
      const pct = Number(this.dataset.offerPct) || 0;
      return pct > 0 ? cents - Math.floor((cents * pct) / 100) : cents;
    }

    /* ---------- Variant state ---------- */

    selectedValues() {
      return this.fieldsets.map((fieldset) => {
        const input = fieldset.querySelector('input:checked');
        return input ? input.dataset.value : null;
      });
    }

    currentVariant() {
      if (this.data.hasOnlyDefault) return this.data.variants[0];
      const values = this.selectedValues();
      if (values.includes(null)) return null;
      return this.data.variants.find((v) => v.options.every((o, i) => o === values[i])) || null;
    }

    firstMissingFieldset() {
      return this.fieldsets.find((fieldset) => !fieldset.querySelector('input:checked'));
    }

    update({ fromUser = false } = {}) {
      const variant = this.currentVariant();
      this.variant = variant;

      const hiddenId = this.querySelector('[data-variant-id]');
      if (hiddenId) hiddenId.value = variant ? variant.id : '';

      if (fromUser) this.fieldsets.forEach((f) => f.classList.remove('is-missing'));
      this.hideError();
      this.renderButtons();

      const price = variant ? variant.price : null;
      const prices = this.data.variants.map((v) => v.price);
      const offer = Number(this.dataset.offerPct) > 0;
      if (variant) {
        // With the offer applied, the struck price is the one before the code
        const was = offer ? Math.max(variant.compare || 0, variant.price) : variant.compare;
        const now = this.offerPrice(variant.price);
        this.querySelector('[data-price]').textContent = formatMoney(now);
        const compare = this.querySelector('[data-compare]');
        const hasCompare = was > now;
        compare.hidden = !hasCompare;
        compare.textContent = hasCompare ? formatMoney(was) : '';
      } else if (offer && Math.min(...prices) === Math.max(...prices)) {
        this.querySelector('[data-price]').textContent = formatMoney(this.offerPrice(prices[0]));
        const compare = this.querySelector('[data-compare]');
        compare.hidden = false;
        compare.textContent = formatMoney(prices[0]);
      }

      const basis = this.offerPrice(price ?? Math.min(...prices));

      const klarna = this.querySelector('[data-klarna]');
      if (klarna) {
        klarna.hidden = !(basis > this.klarnaMin);
        klarna.querySelector('[data-klarna-amount]').textContent = formatMoney(Math.floor(basis / 3));
      }

      const shipping = this.querySelector('[data-shipping]');
      if (shipping) {
        shipping.querySelector('[data-shipping-text]').textContent =
          basis >= this.threshold ? shipping.dataset.included : shipping.dataset.from;
      }

      const low = this.querySelector('[data-low-stock-text]');
      const qty = variant ? variant.qty : null;
      if (qty !== null && qty > 0 && qty <= this.lowStock) {
        low.textContent = qty === 1 ? this.strings.lowStockOne : this.strings.lowStockOther.replace('COUNT', qty);
        low.hidden = false;
      } else {
        low.hidden = true;
      }

      const meta = this.querySelector('[data-sticky-meta]');
      if (meta) {
        const chosen = this.selectedValues().filter(Boolean);
        meta.textContent = [formatMoney(basis), ...(this.data.hasOnlyDefault ? [] : chosen)].join(' · ');
      }

      this.updateRestock(variant);
      this.updateWhatsApp();

      // In the quick-add pop-up the page URL belongs to another page: leave it alone
      if (fromUser && variant && !this.data.hasOnlyDefault && !this.isQuick) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
        if (variant.media) this.showMedia(variant.media);
      }
    }

    /* ---------- Sold out: "Avísame cuando vuelva" ---------- */

    updateRestock(variant) {
      const box = this.querySelector('[data-restock]');
      if (!box) return;
      const soldOut = !this.data.available || (variant && !variant.available);
      box.hidden = !soldOut && !this.restockConfirmed;
      const tags = box.querySelector('[data-restock-tags]');
      if (tags) {
        const slug = variant ? variant.options.join('-').toLowerCase().replace(/[^a-z0-9]+/g, '-') : '';
        tags.value = ['restock', `restock-${this.data.handle}`, slug && `restock-${this.data.handle}-${slug}`]
          .filter(Boolean)
          .join(', ');
      }
    }

    setupRestock() {
      const form = this.querySelector('[data-restock-form]');
      if (!form) return;
      // Shopify blocks background customer-form posts (bot protection), so the
      // form submits normally; on return (?customer_posted=true) we confirm.
      form.addEventListener('submit', () => {
        form.querySelector('button').setAttribute('aria-busy', 'true');
        try {
          window.sessionStorage.setItem('brisato_form_pending', `restock:${this.data.handle}`);
        } catch (_) {
          /* ignore */
        }
        if (window.Shopify && window.Shopify.analytics) {
          window.Shopify.analytics.publish('brisato_restock_request', {
            product: this.data.handle,
            variant: this.variant ? this.variant.options.join(' / ') : '',
          });
        }
      });
      let pending = null;
      try {
        pending = window.sessionStorage.getItem('brisato_form_pending');
      } catch (_) {
        pending = null;
      }
      if (/[?&]customer_posted=true/.test(window.location.search) && pending === `restock:${this.data.handle}`) {
        try {
          window.sessionStorage.removeItem('brisato_form_pending');
        } catch (_) {
          /* ignore */
        }
        const box = this.querySelector('[data-restock]');
        box.hidden = false;
        // Only the confirmation stays: title, form and note go (see .is-done)
        box.classList.add('is-done');
        this.querySelector('[data-restock-done]').hidden = false;
        this.restockConfirmed = true;
        box.scrollIntoView({ block: 'center' });
      }
    }

    /* ---------- WhatsApp: prefilled with product + chosen size ---------- */

    updateWhatsApp() {
      const link = this.querySelector('[data-whatsapp]');
      if (!link) return;
      const chosen = this.selectedValues().filter(Boolean);
      const isSize = this.fieldsets.some((f) => f.dataset.isSize === 'true');
      let message = link.dataset.msg;
      if (chosen.length && !this.data.hasOnlyDefault) message = isSize ? link.dataset.msgSize : link.dataset.msgOption;
      message = message.replace('PRODUCT', link.dataset.product).replace('SIZE', chosen.join(' / '));
      link.href = `https://wa.me/${link.dataset.number}?text=${encodeURIComponent(`${message} ${link.dataset.url}`)}`;
      document.querySelectorAll('[data-whatsapp-float]').forEach((a) => (a.href = link.href));
    }

    buttonLabel() {
      if (!this.data.available) return { text: this.strings.soldOut, disabled: true };
      const variant = this.variant;
      if (!variant) {
        const missing = this.firstMissingFieldset();
        const isSize = missing && missing.dataset.isSize === 'true';
        return { text: isSize ? this.strings.chooseSize : this.strings.chooseOption, disabled: false };
      }
      if (!variant.available) return { text: this.strings.soldOut, disabled: true };
      return { text: this.strings.addToCart, disabled: false };
    }

    renderButtons(override) {
      const state = override || this.buttonLabel();
      this.buttons.forEach((button) => {
        button.querySelector('[data-atc-label]').textContent = state.text;
        button.disabled = state.disabled;
        button.setAttribute('aria-busy', state.busy ? 'true' : 'false');
      });
    }

    /* ---------- Add to cart ---------- */

    async onSubmit(event) {
      event.preventDefault();
      if (this.busy) return;

      if (!this.variant) {
        const fromSticky = event.submitter && event.submitter.hasAttribute('data-sticky-atc');
        if (fromSticky && this.sheet) {
          this.openSheet();
        } else {
          this.pointToMissingOption();
        }
        return;
      }
      if (!this.variant.available) return;
      this.addToCart();
    }

    async addToCart({ buyNow = false } = {}) {
      this.busy = true;
      this.hideError();
      this.renderButtons({ text: this.strings.adding, disabled: false, busy: true });

      const body = new FormData();
      body.append('id', this.variant.id);
      const qtyInput = this.form.querySelector('input[name="quantity"]');
      body.append('quantity', String(Math.max(1, Number(qtyInput && qtyInput.value) || 1)));
      body.append('sections', 'cart-drawer');

      try {
        const response = await fetch(this.dataset.cartAddUrl, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body,
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || result.message || this.strings.error);

        if (buyNow) {
          window.location.href = this.dataset.checkoutUrl || '/checkout';
          return;
        }

        this.renderButtons({ text: this.strings.added, disabled: false });
        this.announce(this.strings.addedAnnouncement);
        // The cart drawer renders itself from result.sections and opens
        document.dispatchEvent(new CustomEvent('cart:item-added', { detail: { item: result, sections: result.sections } }));
        if (!customElements.get('cart-drawer')) this.refreshCartCount();
        window.setTimeout(() => {
          this.busy = false;
          this.renderButtons();
        }, 1800);
      } catch (error) {
        this.busy = false;
        this.renderButtons();
        this.showError(error.message || this.strings.error);
      }
    }

    pointToMissingOption() {
      const fieldset = this.firstMissingFieldset();
      if (!fieldset) return;
      fieldset.classList.remove('is-missing');
      void fieldset.offsetWidth; // restart the nudge animation
      fieldset.classList.add('is-missing');
      fieldset.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const firstEnabled = fieldset.querySelector('input:not(:disabled)');
      if (firstEnabled) window.setTimeout(() => firstEnabled.focus({ preventScroll: true }), 350);
    }

    async refreshCartCount() {
      try {
        const cart = await (await fetch(this.dataset.cartUrl, { headers: { Accept: 'application/json' } })).json();
        document.querySelectorAll('[data-cart-count]').forEach((el) => {
          el.textContent = cart.item_count;
          el.hidden = cart.item_count === 0;
        });
      } catch (_) {
        /* the count catches up on the next page load */
      }
    }

    showError(message) {
      const el = this.querySelector('[data-error]');
      el.textContent = message;
      el.hidden = false;
    }

    hideError() {
      const el = this.querySelector('[data-error]');
      el.hidden = true;
      el.textContent = '';
    }

    announce(message) {
      const el = this.querySelector('[data-announce]');
      el.textContent = '';
      window.setTimeout(() => (el.textContent = message), 50);
    }

    /* ---------- Size sheet (mobile) ---------- */

    setupSheet() {
      this.sheet = this.querySelector('[data-size-sheet]');
      if (!this.sheet) return;
      this.sheet.querySelector('[data-sheet-close]').addEventListener('click', () => this.sheet.close());
      this.sheet.addEventListener('click', (event) => {
        if (event.target === this.sheet) this.sheet.close();
      });
    }

    openSheet() {
      this.renderSheet();
      this.sheet.querySelector('[data-sheet-error]').hidden = true;
      this.sheet.showModal();
    }

    /** Mirror the page's fieldsets as buttons; each button sets the real radio. */
    renderSheet() {
      const container = this.sheet.querySelector('[data-sheet-options]');
      container.replaceChildren();
      this.fieldsets.forEach((fieldset) => {
        const group = document.createElement('div');
        group.className = 'pp-sheet__group';
        if (this.fieldsets.length > 1) {
          const label = document.createElement('p');
          label.className = 'pp-option__label';
          label.textContent = fieldset.querySelector('legend').textContent.trim().replace(/\s+/g, ' ');
          group.append(label);
        }
        const values = document.createElement('div');
        values.className = 'pp-option__values';
        fieldset.querySelectorAll('input[type="radio"]').forEach((input) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'pp-swatch pp-sheet__swatch';
          button.textContent = input.dataset.value;
          button.disabled = input.disabled;
          if (input.hasAttribute('data-soldout')) button.classList.add('is-soldout');
          button.setAttribute('aria-pressed', String(input.checked));
          button.addEventListener('click', () => this.chooseFromSheet(input));
          values.append(button);
        });
        group.append(values);
        container.append(group);
      });
    }

    chooseFromSheet(input) {
      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
      this.renderSheet();
      if (!this.variant) return; // a set: wait for the other garment
      if (!this.variant.available) {
        // Sold-out size: close the sheet and show "Avísame cuando vuelva"
        this.sheet.close();
        const restock = this.querySelector('[data-restock]');
        if (restock) restock.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
      this.sheet.close();
      this.addToCart();
    }

    /* ---------- Size guide ---------- */

    setupSizeGuide() {
      const toggle = this.querySelector('[data-size-guide-toggle]');
      if (!toggle) return;
      const panel = document.getElementById(toggle.getAttribute('aria-controls'));
      toggle.addEventListener('click', () => {
        const open = toggle.getAttribute('aria-expanded') === 'true';
        toggle.setAttribute('aria-expanded', String(!open));
        panel.hidden = open;
      });
    }

    /* ---------- Gallery ---------- */

    setupGallery() {
      this.track = this.querySelector('[data-track]');
      this.thumbs = [...this.querySelectorAll('[data-thumb]')];
      this.setupDeferredMedia();
      this.setupVideos();
      if (!this.track || this.thumbs.length === 0) return;

      this.thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => this.showMedia(thumb.dataset.thumb));
      });

      const slides = [...this.track.children];
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) this.markThumb(entry.target.dataset.mediaId);
          });
        },
        { root: this.track, threshold: 0.6 }
      );
      slides.forEach((slide) => observer.observe(slide));
    }

    /* #51: gallery photos 2+ (and their thumbnails) wait for the first photo.
       They come in once it has loaded, or at once if the visitor swipes or
       taps a thumbnail first, so nobody ever sees an empty slide. */
    setupDeferredMedia() {
      const deferred = [...this.querySelectorAll('img[data-defer]')];
      const posters = [...this.querySelectorAll('video[data-poster]')];
      if (deferred.length === 0 && posters.length === 0) return;
      let done = false;
      const load = () => {
        if (done) return;
        done = true;
        posters.forEach((video) => {
          video.poster = video.dataset.poster;
          video.removeAttribute('data-poster');
        });
        deferred.forEach((img) => {
          if (img.dataset.srcset) img.srcset = img.dataset.srcset;
          if (img.dataset.src) img.src = img.dataset.src;
          img.removeAttribute('data-defer');
        });
      };
      const first = this.track && this.track.querySelector('img:not([data-defer])');
      if (!first || first.complete) {
        requestAnimationFrame(load);
      } else {
        first.addEventListener('load', load, { once: true });
        first.addEventListener('error', load, { once: true });
      }
      ['pointerdown', 'scroll', 'keydown'].forEach((type) =>
        this.track && this.track.addEventListener(type, load, { once: true, passive: true })
      );
      this.thumbs.forEach((thumb) => thumb.addEventListener('pointerdown', load, { once: true }));
    }

    /* #67: product videos play (muted, looping) only while their slide is
       on screen, and pause when it leaves. Tapping toggles; a video the
       visitor paused stays paused. Reduced motion: nothing plays by itself. */
    setupVideos() {
      const videos = [...this.querySelectorAll('video[data-video]')];
      if (videos.length === 0) return;
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const play = (video) => {
        video.muted = true;
        const p = video.play();
        if (p) p.catch(() => {});
      };
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach(({ target: video, isIntersecting, intersectionRatio }) => {
            if (isIntersecting && intersectionRatio >= 0.6) {
              if (!reduce && !video.dataset.userPaused) play(video);
            } else if (!video.paused) {
              video.pause();
            }
          });
        },
        { threshold: [0, 0.6] }
      );
      videos.forEach((video) => {
        const box = video.parentElement;
        const btn = box.querySelector('[data-video-toggle]');
        observer.observe(video);
        video.addEventListener('play', () => {
          box.classList.add('is-playing');
          if (btn) btn.setAttribute('aria-label', btn.dataset.labelPause);
        });
        video.addEventListener('pause', () => {
          box.classList.remove('is-playing');
          if (btn) btn.setAttribute('aria-label', btn.dataset.labelPlay);
        });
        if (btn) {
          btn.addEventListener('click', () => {
            if (video.paused) {
              delete video.dataset.userPaused;
              play(video);
            } else {
              video.dataset.userPaused = '1';
              video.pause();
            }
          });
        }
      });
    }

    markThumb(mediaId) {
      (this.thumbs || []).forEach((thumb) => {
        const active = thumb.dataset.thumb === String(mediaId);
        thumb.toggleAttribute('aria-current', active);
      });
    }

    showMedia(mediaId) {
      const slide = this.track && this.track.querySelector(`[data-media-id="${mediaId}"]`);
      if (!slide) return;
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.track.scrollTo({ left: slide.offsetLeft - this.track.offsetLeft, behavior: reduce ? 'auto' : 'smooth' });
      this.markThumb(mediaId);
    }

    /* ---------- Sticky bar ---------- */

    setupSticky() {
      const bar = this.querySelector('[data-sticky]');
      if (!bar || !this.mainButton) return;
      const mobile = window.matchMedia('(max-width: 989px)');
      let offScreen = false;
      // While the cookie bar is open it covers the bottom of the screen, so the
      // buy bar shows on top of it even when the main button is "in view"
      const update = () => {
        const cookieOpen = document.documentElement.classList.contains('cookie-open');
        if ((offScreen || cookieOpen) && mobile.matches) {
          bar.hidden = false;
          requestAnimationFrame(() => bar.classList.add('is-visible'));
        } else {
          bar.classList.remove('is-visible');
          bar.hidden = true;
        }
      };
      const observer = new IntersectionObserver(([entry]) => {
        offScreen = !entry.isIntersecting;
        update();
      });
      observer.observe(this.mainButton);
      document.addEventListener('cookiebar:change', update);
    }

    /* ---------- Delivery date ---------- */

    refreshDelivery() {
      const item = this.querySelector('[data-delivery]');
      const target = item && item.querySelector('strong');
      if (!target) return;
      const holidays = new Map();
      (item.dataset.holidays || '').split('|').forEach((line) => {
        const match = line.trim().match(/^(\d{4}-\d{2}-\d{2})\s*(.*)$/);
        if (match) holidays.set(match[1], match[2]);
      });
      const { date, holiday } = estimateDelivery(holidays);
      const note = item.querySelector('[data-holiday-text]');
      if (note) {
        note.textContent = holiday
          ? item.dataset.holidayNote.replace('HDAY', holiday.day).replace('HNAME', holiday.name)
          : '';
      }
      const weekdays = item.dataset.weekdays.split(',');
      const months = item.dataset.months.split(',');
      target.textContent = item.dataset.format
        .replace('WDAY', weekdays[date.getDay()])
        .replace('DNUM', date.getDate())
        .replace('MNAME', months[date.getMonth()]);
    }
  }

  customElements.define('product-page', ProductPage);
})();
